import { MotionConfig } from 'motion/react';
import { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { useBreakpointsContext } from '@air/provider-media-query';
import { cn } from '@air/utils-tailwind';

import { Banners } from '~/components/Layouts/HomeLayout/Banners';
import { BoardImportDropTarget } from '~/components/Shared/Drag/BoardImportDropTarget/BoardImportDropTarget';
import { PrivatePageSeoTitle } from '~/components/v2/components/shared/seo/PrivatePageSeoTitle';
import { MainSideNav } from '~/components/v2/components/workspace/main-side-nav/MainSideNav';
import { WorkspaceNavOverlayScrim } from '~/components/v2/components/workspace/main-side-nav/WorkspaceNavOverlayScrim';
import { PrivateOnboardingIntroMount } from '~/components/v2/components/workspace/onboarding-intro/PrivateOnboardingIntroMount';
import { WorkspaceSubSideNav } from '~/components/v2/components/workspace/sub-side-nav/WorkspaceSubSideNav';
import { useWorkspaceAgentPanelCanvasOpen } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelCanvasOpen';
import { PrivateWorkspaceAgentPanel } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanel';
import { PrivateWorkspaceAgentSpace } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentSpace';
import { LibraryActions } from '~/components/v2/components/workspace/workspace-header/LibraryActions';
import { WorkspaceHeader } from '~/components/v2/components/workspace/workspace-header/WorkspaceHeader';
import { WorkspaceImports } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImports';
import { WorkspaceNotification } from '~/components/v2/components/workspace/workspace-notification/WorkspaceNotification';
import { WorkspaceSidebarToken } from '~/components/v2/components/workspace/workspace-sidebar/getWorkspaceSidebarEventToken';
import { WorkspaceUploads } from '~/components/v2/components/workspace/workspace-uploads/WorkspaceUploads';
import { useIsCanvasVisible } from '~/components/v2/hooks/useIsCanvasVisible';
import { useUI2026Styles } from '~/components/v2/hooks/useUI2026Styles';
import { useWorkspaceNavPushMode } from '~/components/v2/hooks/useWorkspaceNavPushMode';
import { useMainSideNavStore } from '~/components/v2/store/features/workspace/useMainSideNavStore';
import { useSubSideNavStore } from '~/components/v2/store/features/workspace/useSubSideNavStore';
import { useWorkspaceNotificationsStore } from '~/components/v2/store/features/workspace/useWorkspaceNotificationsStore';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { useWorkspaceSidebarPushStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarPushStore';
import { useWorkspaceSidebarStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarStore';
import { useClearCanvasVersionTargetOnLeave } from '~/hooks/canvases/useClearCanvasVersionTargetOnLeave';
import { useClearUi2026SplitPanelCanvas } from '~/hooks/canvases/useClearUi2026SplitPanelCanvas';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getWorkspaceContentStyle } from '~/utils/getWorkspaceContentStyle';

const WINDOW_RESIZE_DEBOUNCE_MS = 150;

export type PrivateDefaultLayoutProps = {
  /** Pages still pass this through `getLayout`. This shell does not render it. */
  bottomBar?: ReactNode;
  children: ReactNode;
  getLibraryActions?: boolean;
  headerActions?: ReactNode;
  search?: ReactNode;
};

export const PrivateDefaultLayout = memo(
  ({ children, getLibraryActions = false, headerActions, search }: PrivateDefaultLayoutProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
    const isCanvasVisible = useIsCanvasVisible();
    useClearUi2026SplitPanelCanvas({ enabled: generativeCanvasEnabled });
    useClearCanvasVersionTargetOnLeave();

    const boardId = useURLBoardIdSelector();
    const rightPanelOpen = useWorkspaceRightPanelOpen();
    const isLeftNavResizing = useWorkspaceSidebarStore((state) => state.isResizing);
    const isSubSideNavResizing = useSubSideNavStore((state) => state.isResizing);
    const { isWorkspaceNavPushMode } = useWorkspaceNavPushMode();
    const { isAboveXXXLScreen } = useBreakpointsContext();
    useUI2026Styles({ force: true });
    const [isWindowResizing, setIsWindowResizing] = useState(false);
    const windowResizeTimerRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
      const handleResize = () => {
        setIsWindowResizing(true);
        clearTimeout(windowResizeTimerRef.current);
        windowResizeTimerRef.current = setTimeout(() => setIsWindowResizing(false), WINDOW_RESIZE_DEBOUNCE_MS);
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        clearTimeout(windowResizeTimerRef.current);
      };
    }, []);
    const leftSidebarOpen = useWorkspaceSidebarStore((state) => state.open);
    const sidebarPushOpen = useWorkspaceSidebarPushStore((s) => s.isOpen);
    const closePush = useWorkspaceSidebarPushStore((s) => s.closePush);
    const deleteLockToken = useWorkspaceSidebarPushStore((s) => s.deleteLockToken);
    const resetPush = useWorkspaceSidebarPushStore((s) => s.reset);
    const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);
    const boardViewCollapsed = useWorkspaceRightPanelStore((state) => state.boardViewCollapsed);
    const setBoardViewCollapsed = useWorkspaceRightPanelStore((state) => state.setBoardViewCollapsed);
    const setLeftSidebarOpen = useWorkspaceSidebarStore((state) => state.setOpen);
    const setPortal = useWorkspaceSelectionBarStore((state) => state.setPortal);
    const isNonBoardPanelCollapsed = !boardId && rightPanelOpen && !isFullscreen && boardViewCollapsed;

    useEffect(() => {
      if (leftSidebarOpen) {
        resetPush();
      }
    }, [leftSidebarOpen, resetPush]);

    const leftSidebarOpenRef = useRef(leftSidebarOpen);
    leftSidebarOpenRef.current = leftSidebarOpen;

    const wasFullscreenRef = useRef(isFullscreen);
    const sidebarWasOpenBeforeFullscreenRef = useRef<boolean>(false);
    useEffect(() => {
      const wasFullscreen = wasFullscreenRef.current;
      wasFullscreenRef.current = isFullscreen;

      if (isFullscreen && !wasFullscreen) {
        sidebarWasOpenBeforeFullscreenRef.current = leftSidebarOpenRef.current;
        setLeftSidebarOpen(false);
      } else if (!isFullscreen && wasFullscreen) {
        // If the canvas is also closing, always pin the nav.
        // If the user is just exiting fullscreen while keeping the canvas open, restore pre-fullscreen state.
        setLeftSidebarOpen(rightPanelOpen ? sidebarWasOpenBeforeFullscreenRef.current : true);
      }
    }, [isFullscreen, rightPanelOpen, setLeftSidebarOpen]);

    // Auto-collapse left nav when right panel opens on screens below 1600px.
    // Only restore sidebar on canvas close if it was open when canvas opened —
    // don't force it open if the user had already closed it manually.
    const prevRightPanelOpenRef = useRef(rightPanelOpen);
    const sidebarWasOpenRef = useRef<boolean>(false);
    useEffect(() => {
      const wasOpen = prevRightPanelOpenRef.current;
      prevRightPanelOpenRef.current = rightPanelOpen;

      if (rightPanelOpen && !wasOpen && !isAboveXXXLScreen) {
        sidebarWasOpenRef.current = leftSidebarOpenRef.current;
        setLeftSidebarOpen(false);
      }
      if (!rightPanelOpen && wasOpen) {
        // biome-ignore lint/suspicious/noUnnecessaryConditions: `.current` is written on earlier panel-open renders; Biome only sees the initial `false`.
        if (sidebarWasOpenRef.current) {
          setLeftSidebarOpen(true);
        }
        sidebarWasOpenRef.current = false;
        setBoardViewCollapsed(false);
      }
    }, [rightPanelOpen, isAboveXXXLScreen, setLeftSidebarOpen, setBoardViewCollapsed]);

    useEffect(() => {
      if (isWorkspaceNavPushMode && leftSidebarOpen) {
        sidebarWasOpenRef.current = true;
        setLeftSidebarOpen(false);
      }
    }, [isWorkspaceNavPushMode, leftSidebarOpen, setLeftSidebarOpen]);

    useIsomorphicLayoutEffect(() => {
      const isPinned = leftSidebarOpen && !isWorkspaceNavPushMode && !isFullscreen;
      document.documentElement.style.setProperty('--left-nav-width', isPinned ? 'var(--sidebar-width)' : '0px');
    }, [leftSidebarOpen, isWorkspaceNavPushMode, isFullscreen]);

    const { canvasOpen } = useWorkspaceAgentPanelCanvasOpen();

    const contentStyle = getWorkspaceContentStyle({
      rightPanelOpen,
      isFullscreen,
      boardViewCollapsed,
      reserveCollapsedHandleStrip: !!boardId,
    });

    const handleAppHover = useCallback(() => {
      const isNotificationsOpen = useWorkspaceNotificationsStore.getState().open;

      if (isWorkspaceNavPushMode && isNotificationsOpen) {
        return;
      }

      if (isWorkspaceNavPushMode || !leftSidebarOpen) {
        deleteLockToken(WorkspaceSidebarToken.Notifications);
        closePush();
      }
    }, [isWorkspaceNavPushMode, leftSidebarOpen, deleteLockToken, closePush]);

    const handleAppClick = useCallback(() => {
      useMainSideNavStore.getState().setActiveOverlay(null);
    }, []);

    return (
      <div
        className="group/layout flex size-full flex-col ads:bg-ads-gray-4 bg-gray-4 ui2026:p-1"
        data-sidebar-overlay={sidebarPushOpen && !leftSidebarOpen ? 'open' : undefined}
      >
        <PrivatePageSeoTitle isFallbackTitle />
        <PrivateOnboardingIntroMount />
        {currentWorkspace && (
          // PendingEmailBanner keeps an sr-only live region mounted after dismiss, so :empty alone can't drop the padding.
          <div className="flex shrink-0 flex-col ui2026:gap-1 empty:hidden ui2026:has-[>:not(.sr-only)]:pb-1">
            <Banners />
          </div>
        )}
        <div className="flex min-h-0 min-w-0 grow ui2026:flex-row flex-col ui2026:gap-1">
          <div
            className="group/main-side-nav-overlay ui2026:relative flex min-h-0 min-w-0 grow flex-col ui2026:overflow-hidden ui2026:rounded-12 ads:bg-base ui2026:bg-gray-1"
            data-workspace-chrome-root
          >
            {!rightPanelOpen && (
              // biome-ignore lint/a11y/noStaticElementInteractions: click dismisses open main-side-nav overlays; header controls stay the keyboard targets.
              // biome-ignore lint/a11y/useKeyWithClickEvents: overlay dismiss is pointer-only, matching MainSideNav.
              <div // NOSONAR -- pointer-only overlay dismiss; header stays the keyboard target
                onClick={handleAppClick} // NOSONAR -- pointer-only overlay dismiss
                onPointerEnter={handleAppHover}
              >
                <WorkspaceHeader
                  actions={
                    <>
                      {headerActions}
                      {getLibraryActions && <LibraryActions />}
                    </>
                  }
                  search={search}
                />
              </div>
            )}
            {/* overflow-x-hidden computes overflow-y to auto and flashes a page scrollbar when the overlay rail expands. */}
            <div className="relative flex min-h-0 grow overflow-hidden">
              <MainSideNav />
              <WorkspaceSubSideNav />
              <WorkspaceNotification />
              <WorkspaceImports />
              <WorkspaceUploads />

              <div
                className={cn(
                  'relative flex min-h-0 grow overflow-hidden group-data-[sidebar-overlay=open]/layout:ml-[var(--sidebar-width)]',
                  rightPanelOpen && 'pt-2 ui2026:pt-0',
                  isLeftNavResizing || isSubSideNavResizing || isWindowResizing || leftSidebarOpen
                    ? 'transition-none'
                    : 'transition-[margin-left] duration-200 ease-out',
                  isCanvasVisible && rightPanelOpen && (isWorkspaceNavPushMode || sidebarPushOpen) && 'w-full shrink-0',
                )}
              >
                <div
                  className={cn(
                    // Row-flex cross size defaults to `min-height: auto`, so this column
                    // grows with the page and ui2026's overflow-hidden parent clips it.
                    'relative flex ui2026:min-h-0 flex-col overflow-hidden',
                    !isNonBoardPanelCollapsed && 'w-full',
                  )}
                  data-workspace-content
                  style={isCanvasVisible ? contentStyle : undefined}
                >
                  <div className="flex min-h-0 grow overflow-hidden">
                    {/* biome-ignore lint/a11y/noStaticElementInteractions: click dismisses open main-side-nav overlays; gallery/board content stays the keyboard target. */}
                    {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay dismiss is pointer-only, matching MainSideNav. */}
                    <div // NOSONAR -- pointer-only overlay dismiss; content stays the keyboard target
                      className={cn(
                        // `ui2026:min-h-0` lets this row item shrink below its content so nested
                        // `overflow-auto` regions (settings `<main>`) become the scrollport.
                        // Without it, ui2026's `overflow-hidden` ancestors clip the page.
                        'relative flex ui2026:min-h-0 min-w-0 grow pb-2 ui2026:pb-0',
                        !isNonBoardPanelCollapsed && 'gap-2 ui2026:gap-0 px-2 ui2026:px-0',
                      )}
                      onClick={handleAppClick} // NOSONAR -- pointer-only overlay dismiss
                      onPointerEnter={handleAppHover}
                      ref={setPortal}
                    >
                      {isCanvasVisible && !isNonBoardPanelCollapsed && <BoardImportDropTarget boardId={boardId} />}
                      {!isNonBoardPanelCollapsed && children}
                    </div>
                  </div>
                </div>
                {/* Mounted here rather than per page so the selection bar survives navigation. */}
                {/* `useReducedMotionConfig` defaults to never; `user` keeps OS reduced-motion for the slide. */}
                <MotionConfig reducedMotion="user">
                  <PrivateWorkspaceAgentSpace canvasOpen={canvasOpen} />
                </MotionConfig>
              </div>
            </div>
            <WorkspaceNavOverlayScrim />
          </div>
          <PrivateWorkspaceAgentPanel canvasOpen={canvasOpen} />
        </div>
      </div>
    );
  },
);

PrivateDefaultLayout.displayName = 'PrivateDefaultLayout';
